<template>
  <v-dialog dark :value="value" style="z-index: 100000" fullscreen transition="dialog-bottom-transition">
    <div class="converter-background">
      <div class="gradient-bg"></div>
      <v-toolbar dark color="transparent" flat class="pt-2">
        <v-btn icon dark @click="$emit('close')">
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <v-progress-linear :value="(step / 4) * 100" color="primary" height="6" class="mx-4 rounded-pill"></v-progress-linear>
      </v-toolbar>

      <v-window v-model="step" class="fill-height">
        <v-window-item :value="1" class="fill-height">
          <div class="step-wrapper">
            <h1 class="step-title">Pläne upgraden</h1>
            <p class="step-subtitle">Wähle die alten Pläne, die du zu einem neuen, modernen Plan zusammenführen möchtest.</p>
            <v-list-item-group v-model="selectedPlans" multiple class="mb-4 content-scroll">
              <transition-group name="list-anim" tag="div">
                <v-list-item
                  v-for="plan in oldPlans.filter((plan) => plan.exercises.length)"
                  :key="plan._id"
                  :value="plan"
                  class="plan-item pa-3 mb-3">
                  <template v-slot:default="{ active }">
                    <v-list-item-content>
                      <v-list-item-title class="font-weight-bold white--text">{{ plan.form.title }}</v-list-item-title>
                      <v-list-item-subtitle class="mt-1">
                        {{ formatDate(plan.form.ts) }} • {{ plan.exercises ? plan.exercises.length : 0 }} Übungen
                      </v-list-item-subtitle>
                    </v-list-item-content>
                    <v-list-item-action>
                      <v-icon v-if="active" color="primary" size="32">mdi-check-circle</v-icon>
                      <v-icon v-else size="32">mdi-circle-outline</v-icon>
                    </v-list-item-action>
                  </template>
                </v-list-item>
              </transition-group>
            </v-list-item-group>
            <div class="step-actions">
              <v-btn color="primary" x-large block rounded @click="processPlans" :disabled="selectedPlans.length === 0">Weiter</v-btn>
            </div>
          </div>
        </v-window-item>
        <v-window-item :value="2" class="fill-height">
          <div class="step-wrapper">
            <h1 class="step-title">Doppelte Übungen</h1>
            <p class="step-subtitle">
              Einige Übungen sind in mehreren Plänen vorhanden. Wähle die Versionen mit den Sätzen aus, die du behalten möchtest.
            </p>
            <div class="content-scroll">
              <transition-group name="list-anim" tag="div">
                <div v-for="(versions, exerciseId) in duplicateExercises" :key="versions[0].title" class="mb-5 duplicate-group">
                  <h3 class="mb-3 font-weight-bold white--text">{{ versions[0].title }}</h3>
                  <div v-for="(exercise, index) in versions" :key="index + 'test'">
                    <v-checkbox v-model="resolvedDuplicates[exerciseId]" :value="exercise" class="mb-2" hide-details>
                      <template v-slot:label>
                        <v-card class="glass-card exercise-card-dest elevation-6 ml-2 pa-2" style="width: 100%">
                          <div class="d-flex align-center">
                            <v-img
                              :src="getImageForExercise(exercise)"
                              style="max-width: 50px"
                              class="rounded-lg"
                              height="50px"
                              width="50px"
                              cover
                              position="top center" />
                            <div class="exercise-content">
                              <div class="font-weight-bold white--text text-subtitle-1">Aus: {{ exercise.sourcePlanTitle }}</div>
                              <div v-if="exercise.sets && exercise.sets.length > 0" class="mt-1">
                                <span class="caption grey--text">{{ exercise.sets.length }} Sätze / {{ formatSetDetails(exercise.sets) }}</span>
                              </div>
                            </div>
                          </div>
                        </v-card>
                      </template>
                    </v-checkbox>
                  </div>
                </div>
              </transition-group>
            </div>
            <div class="step-actions">
              <v-btn color="primary" x-large block rounded @click="finalizeExerciseSelection" :disabled="isDuplicateSelectionInvalid"
                >Bestätigen & Weiter</v-btn
              >
              <v-btn text @click="step = 1" class="mt-2">Zurück</v-btn>
            </div>
          </div>
        </v-window-item>
        <v-window-item :value="3" class="fill-height">
          <div class="step-wrapper">
            <h1 class="step-title">Split-Typ</h1>
            <p class="step-subtitle">Wähle die Struktur deines neuen Plans. Das bestimmt deine Trainingstage.</p>
            <div class="content-scroll">
              <v-item-group v-model="selectedSplitIndex" class="mb-6" mandatory>
                <transition-group name="list-anim" tag="div" class="row">
                  <v-col v-for="(split, index) in splitItems" class="pb-0" cols="12" :key="split.info">
                    <v-item v-slot="{ active, toggle }">
                      <v-card @click="toggle" class="split-item" :class="{ 'split-item--active': active }">
                        <div class="d-flex align-center pa-4">
                          <div class="flex-grow-1">
                            <div class="font-weight-bold text-h6">{{ split.text }}</div>
                            <p class="caption mb-0 text--lighten-2">{{ split.info }}</p>
                          </div>
                          <v-icon v-if="active" size="32" color="white">mdi-check-decagram</v-icon>
                        </div>
                      </v-card>
                    </v-item>
                  </v-col>
                  <v-col class="pb-0" cols="12" key="custom-split">
                    <v-item v-slot="{ active, toggle }">
                      <v-card @click="startCustomSplitCreation" class="split-item" :class="{ 'split-item--active': active }">
                        <div class="d-flex align-center pa-4">
                          <div class="flex-grow-1">
                            <div class="font-weight-bold text-h6">Eigener Split erstellen</div>
                            <p class="caption mb-0 text--lighten-2">Definiere deine eigenen Trainingstage (z.B. Push, Pull, Beine).</p>
                          </div>
                          <v-icon size="32" color="white">{{ active ? "mdi-check-decagram" : "mdi-plus-circle-outline" }}</v-icon>
                        </div>
                      </v-card>
                    </v-item>
                  </v-col>
                </transition-group>
              </v-item-group>
            </div>
            <div class="step-actions">
              <v-btn color="primary" x-large block rounded @click="selectSplitType" :disabled="selectedSplitIndex === null">Weiter</v-btn>
              <v-btn text @click="step = 2" class="mt-2">Zurück</v-btn>
            </div>
          </div>
        </v-window-item>

        <v-window-item :value="4" class="fill-height">
          <div class="step-wrapper" v-if="selectedSplitType">
            <h1 class="step-title">Übungen zuweisen</h1>
            <p class="step-subtitle">Füge die Übungen den richtigen Trainingstagen hinzu.</p>
            <div class="content-scroll">
              <h3 class="font-weight-bold mb-3 mt-2 white--text">Deine Splits</h3>
              <v-expansion-panels accordion v-model="panel" multiple>
                <v-expansion-panel v-for="(split, index) in selectedSplitType.workouts" :key="index + 'oioi'" class="split-panel mb-2">
                  <v-expansion-panel-header class="split-panel-header">
                    <span class="font-weight-bold">{{ split.title }}</span>
                    <div class="ml-auto chip-container">
                      <v-chip small color="primary" class="ml-2">{{ split.exercises.length }}</v-chip>
                    </div>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <div v-if="split.exercises.length === 0" class="text-center grey--text py-4">
                      Tippe unten auf eine Übung, um sie hier hinzuzufügen.
                    </div>
                    <transition-group name="list-anim" tag="div" class="mt-2">
                      <v-card
                        v-for="(exercise, exIndex) in split.exercises"
                        :key="exercise.instanceId"
                        class="glass-card assigned-exercise-card elevation-4 mb-2">
                        <div class="d-flex align-center pa-2">
                          <v-img
                            :src="getImageForExercise(exercise)"
                            style="max-width: 50px"
                            class="rounded-lg flex-shrink-0"
                            height="50px"
                            width="50px"
                            cover
                            position="top center" />
                          <div class="exercise-content">
                            <div class="font-weight-bold white--text text-subtitle-1">{{ exercise.title }}</div>
                            <span class="caption grey--text">{{ formatSetDetails(exercise.sets) }}</span>
                          </div>
                          <v-btn icon @click="unassignExercise(exercise, index, exIndex)">
                            <v-icon color="red">mdi-minus-circle</v-icon>
                          </v-btn>
                        </div>
                      </v-card>
                    </transition-group>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>

              <h3 class="font-weight-bold my-3 white--text">Verfügbare Übungen ({{ finalExercises.length }})</h3>
              <div v-if="finalExercises.length === 0" class="text-center grey--text py-4">Super! Alle Übungen zugewiesen.</div>
              <transition-group name="list-anim" tag="div">
                <div v-for="(exercise, index) in finalExercises" :key="exercise.instanceId" class="mb-2">
                  <v-card class="glass-card exercise-card-source elevation-4" @click="openAssignDialog(exercise, index)">
                    <div class="d-flex align-center pa-2">
                      <v-img
                        :src="getImageForExercise(exercise)"
                        style="max-width: 60px"
                        class="rounded-lg flex-shrink-0"
                        height="60px"
                        width="60px"
                        cover
                        position="top center" />
                      <div class="exercise-content">
                        <div class="font-weight-bold white--text text-subtitle-1">{{ exercise.title }}</div>
                        <span class="caption grey--text">{{ formatSetDetails(exercise.sets) }}</span>
                      </div>
                      <v-icon color="primary" size="32">mdi-plus-circle-outline</v-icon>
                    </div>
                  </v-card>
                </div>
              </transition-group>
            </div>

            <v-divider class="my-6"></v-divider>

            <div class="step-actions">
              <v-btn color="primary" x-large block rounded @click="prepareAndRedirect">Plan erstellen</v-btn>
              <v-btn text @click="step = 3" class="mt-2">Zurück</v-btn>
            </div>
          </div>
        </v-window-item>
      </v-window>
    </div>

    <v-bottom-sheet v-model="assignDialog" dark inset>
      <v-sheet dark class="text-center assign-sheet pa-4">
        <div v-if="exerciseToAssign" class="mb-4">
          <div class="text-h6 mb-1">"{{ exerciseToAssign.title }}"</div>
          <div class="caption grey--text text--darken-1">Welchen Splits möchtest du die Übung zuweisen?</div>
        </div>
        <v-list-item-group dark v-if="selectedSplitType" v-model="assignedSplits" multiple class="mb-4">
          <v-list-item
            dark
            v-for="(split, index) in selectedSplitType.workouts"
            :key="index + '-test2'"
            :value="index"
            class="plan-item mb-2"
            active-class="plan-item--active">
            <template v-slot:default="{ active }">
              <v-list-item-content>
                <v-list-item-title>{{ split.title }}</v-list-item-title>
              </v-list-item-content>
              <v-list-item-action>
                <v-icon v-if="active" color="primary">mdi-check-circle</v-icon>
                <v-icon v-else>mdi-circle-outline</v-icon>
              </v-list-item-action>
            </template>
          </v-list-item>
        </v-list-item-group>
        <v-btn x-large block rounded color="primary" @click="assignExercise">Übernehmen</v-btn>
      </v-sheet>
    </v-bottom-sheet>
    <v-dialog dark v-model="addWorkoutToSplit" persistent max-width="450px" content-class="custom-split-dialog">
      <v-card class="custom-split-dialog-card">
        <v-card-title class="d-flex flex-column align-start">
          <v-icon color="primary" large class="mb-2">mdi-pencil-ruler</v-icon>
          <span class="text-h5 font-weight-bold">Custom Split Builder</span>
        </v-card-title>
        <v-card-subtitle>Benenne deine individuellen Trainingstage.</v-card-subtitle>
        <v-card-text>
          <v-text-field
            solo
            dark
            flat
            label="z.B. Push, Pull, Beine..."
            v-model="addNewSplitText"
            @keydown.enter.prevent="addWorkoutDay"
            class="mb-3">
            <template v-slot:append>
              <v-btn icon color="primary" @click="addWorkoutDay" :disabled="!addNewSplitText.trim()">
                <v-icon>mdi-plus-circle</v-icon>
              </v-btn>
            </template>
          </v-text-field>
          <v-expand-transition>
            <div v-if="customWorkouts.length > 0">
              <v-chip-group column class="custom-chip-group">
                <v-chip v-for="(workout, index) in customWorkouts" :key="index" close @click:close="removeWorkoutDay(index)" class="custom-chip">
                  {{ workout.title }}
                </v-chip>
              </v-chip-group>
            </div>
          </v-expand-transition>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-btn text large @click="cancelCustomSplitCreation" class="font-weight-bold">Abbrechen</v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            depressed
            large
            rounded
            @click="finishCustomSplitCreation"
            :disabled="customWorkouts.length === 0"
            class="font-weight-bold">
            <v-icon left>mdi-check</v-icon>
            Fertig
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="showConfirmDialog" max-width="450" persistent dark>
      <v-card class="glass-card pa-4">
        <v-card-title class="font-weight-bold justify-center">
          <v-icon left color="orange">mdi-alert-circle-outline</v-icon>
          Übungen übrig
        </v-card-title>
        <v-card-text class="white--text text-center mt-4">
          Es sind noch {{ finalExercises.length }} Übungen nicht zugewiesen. Wenn du fortfährst, werden diese verworfen.
        </v-card-text>
        <v-card-actions class="pa-4 d-flex justify-space-around">
          <v-btn text color="grey" @click="showConfirmDialog = false">Abbrechen</v-btn>
          <v-btn color="primary" depressed @click="confirmProceed">Trotzdem fortfahren</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import { mapActions, mapGetters } from "vuex"
import moment from "moment"
import draggable from "vuedraggable"

export default {
  name: "PlanConverter",
  components: { draggable },
  props: {
    value: { type: Boolean, required: true },
    isStandalone: { type: Boolean, default: true },
  },
  data() {
    return {
      uniqueExercises: [], // <-- DIESE ZEILE HINZUFÜGEN

      step: 1,
      selectedPlans: [],
      duplicateExercises: {},
      resolvedDuplicates: {},
      finalExercises: [],
      selectedSplitType: null,
      selectedSplitIndex: null,
      assignDialog: false,
      exerciseToAssign: null,
      exerciseToAssignIndex: null,
      assignedSplits: [],
      panel: [],
      addWorkoutToSplit: false,
      addNewSplitText: "",
      customWorkouts: [],
      showConfirmDialog: false,
      splitItems: [
        {
          text: "Ganzkörper-Training",
          value: "fullBodySplit",
          info: "Jede Trainingseinheit trainiert den gesamten Körper.",
          workouts: [{ title: "Ganzkörper", exercises: [] }],
        },
        {
          text: "Oberkörper-/Unterkörper Split",
          value: "upperLowerSplit",
          info: "Ein Tag für den Oberkörper, der andere für den Unterkörper.",
          workouts: [
            { title: "Oberkörper", exercises: [] },
            { title: "Unterkörper", exercises: [] },
          ],
        },
        {
          text: "Push/Pull/Beine-Split",
          value: "pushPullLegSplit",
          info: "Drückende, ziehende Muskeln und Beine an separaten Tagen.",
          workouts: [
            { title: "Push", exercises: [] },
            { title: "Pull", exercises: [] },
            { title: "Beine", exercises: [] },
          ],
        },
        {
          text: "Bro-Split",
          value: "broSplit",
          info: "Jede Muskelgruppe hat einen eigenen Trainingstag.",
          workouts: [
            { title: "Brust", exercises: [] },
            { title: "Rücken", exercises: [] },
            { title: "Schultern", exercises: [] },
            { title: "Beine", exercises: [] },
            { title: "Arme", exercises: [] },
          ],
        },
      ],
    }
  },
  computed: {
    ...mapGetters("runWorkoutStore", { oldPlans: "oldWorkoutPlans" }),
    ...mapGetters("deviceStore", ["exerciseList"]),
    isDuplicateSelectionInvalid() {
      if (Object.keys(this.duplicateExercises).length === 0) return false
      return Object.values(this.resolvedDuplicates).some((selection) => selection.length === 0)
    },
  },
  methods: {
    ...mapActions("runWorkoutStore", ["setPlanForConversion"]),
    formatDate(timestamp) {
      return moment.unix(Number(timestamp)).format("DD.MM.YYYY")
    },
    getImageForExercise(exercise) {
      const exId = exercise.exercise_id ? exercise.exercise_id._id || exercise.exercise_id : null
      const fullEx = this.exerciseList.find((e) => e._id === exId)
      if (fullEx && fullEx.picture) return `/img/uebungen/${fullEx.picture.replace(/\.[^/.]+$/, "")}.webp`
      if (fullEx && fullEx.manual && fullEx.manual[0]?.picture) return `/img/uebungen/${fullEx.manual[0].picture.replace(/\.[^/.]+$/, "")}.webp`
      return "/img/einmalig/imageUploadPlaceholder.jpg"
    },
    formatSetDetails(sets) {
      if (!sets || sets.length === 0) return "Keine Sätze"
      const firstSet = sets[0]
      const settings = Array.isArray(firstSet) ? firstSet : firstSet.settings
      if (!settings) return "Keine Satzdetails"

      const weight = settings.find((s) => s.text === "Gewicht")?.value
      const minReps = settings.find((s) => s.text === "Min Wiederholungen")?.value
      const maxReps = settings.find((s) => s.text === "Max Wiederholungen")?.value
      let details = []
      if (weight) details.push(`${weight}kg`)
      if (minReps && maxReps) details.push(`${minReps}-${maxReps} Wdh.`)
      else if (maxReps) details.push(`${maxReps} Wdh.`)
      return details.join(" / ")
    },
    // NEUE METHODE ZUM BEREINIGEN DER SATZ-WERTE
    cleanSetSettings(sets) {
      if (!sets || !Array.isArray(sets)) {
        return sets
      }

      const fieldsToClean = ["Gewicht", "Pause", "Distanz", "Dauer"]

      return sets.map((set) => {
        if (set.settings && Array.isArray(set.settings)) {
          const newSettings = set.settings.map((setting) => {
            const isNumericField = fieldsToClean.includes(setting.text) || (setting.text && setting.text.includes("Wiederholungen"))

            if (isNumericField && setting.value && typeof setting.value === "string") {
              let cleanedValue
              if (setting.text === "Gewicht") {
                cleanedValue = setting.value.replace(/[^0-9.]/g, "")
              } else {
                cleanedValue = setting.value.replace(/[^0-9]/g, "")
              }
              return { ...setting, value: cleanedValue }
            }
            return setting
          })
          return { ...set, settings: newSettings }
        }
        return set
      })
    },
    processPlans() {
      const exerciseMap = new Map()
      this.selectedPlans.forEach((plan) => {
        if (plan.exercises) {
          plan.exercises.forEach((ex_old) => {
            const exerciseId = ex_old.exercise_id ? ex_old.exercise_id._id || ex_old.exercise_id : null
            if (!exerciseId) return

            const fullExercise_from_library = this.exerciseList.find((e) => e._id === exerciseId)
            if (!fullExercise_from_library) return

            let mergedExercise = JSON.parse(JSON.stringify(fullExercise_from_library))

            if (ex_old.general_exercise_settings && mergedExercise.general_exercise_settings) {
              ex_old.general_exercise_settings.forEach((old_setting) => {
                if (old_setting.value) {
                  const new_setting = mergedExercise.general_exercise_settings.find((s) => s.text === old_setting.text)
                  if (new_setting) {
                    new_setting.value = old_setting.value
                  }
                }
              })
            }

            // --- START: NEUE LOGIK FÜR SENSO PRO ---

            // Prüft, ob es sich um die "Senso Pro" Übung handelt.
            if (mergedExercise.title === "Senso Pro") {
              // 1. Entfernt alle alten Sätze, da sie nicht mehr benötigt werden.
              mergedExercise.sets = []

              // 2. Setzt die Übung auf "konfiguriert", damit das grüne Häkchen erscheint.
              //    Wir gehen davon aus, dass die übernommenen Geräteeinstellungen gültig sind.
              mergedExercise.isConfigured = true
            } else if (ex_old.sets) {
              // Die bisherige Logik für alle anderen Übungen bleibt bestehen.
              mergedExercise.sets = this.cleanSetSettings(ex_old.sets)
            } else {
              mergedExercise.sets = []
            }

            // --- ENDE: NEUE LOGIK FÜR SENSO PRO ---

            const exerciseWithMeta = {
              ...mergedExercise,
              sourcePlanTitle: plan.form.title,
              instanceId: `${plan._id}-${exerciseId}`,
            }

            if (exerciseMap.has(exerciseId)) {
              exerciseMap.get(exerciseId).push(exerciseWithMeta)
            } else {
              exerciseMap.set(exerciseId, [exerciseWithMeta])
            }
          })
        }
      })

      this.uniqueExercises = []
      const duplicates = {}
      this.resolvedDuplicates = {}

      exerciseMap.forEach((versions, id) => {
        if (versions.length > 1) {
          duplicates[id] = versions
          this.$set(this.resolvedDuplicates, id, [versions[0]])
        } else {
          this.uniqueExercises.push(versions[0])
        }
      })

      this.duplicateExercises = duplicates

      if (Object.keys(duplicates).length > 0) {
        this.step = 2
      } else {
        this.finalExercises = [...this.uniqueExercises]
        this.step = 3
      }
    },
    finalizeExerciseSelection() {
      const resolved = Object.values(this.resolvedDuplicates).flat()
      this.finalExercises = [...this.uniqueExercises, ...resolved]
      this.step = 3
    },
    startCustomSplitCreation() {
      this.customWorkouts = []
      this.addWorkoutToSplit = true
    },
    addWorkoutDay() {
      if (!this.addNewSplitText || !this.addNewSplitText.trim()) return
      this.customWorkouts.push({ title: this.addNewSplitText.trim(), exercises: [] })
      this.addNewSplitText = ""
    },
    removeWorkoutDay(index) {
      this.customWorkouts.splice(index, 1)
    },
    cancelCustomSplitCreation() {
      this.addWorkoutToSplit = false
      this.addNewSplitText = ""
      this.customWorkouts = []
    },
    finishCustomSplitCreation() {
      const customSplit = {
        text: "Eigener Split",
        value: "customSplit",
        info: "Ein benutzerdefinierter Split.",
        workouts: this.customWorkouts,
      }
      this.selectedSplitType = JSON.parse(JSON.stringify(customSplit))
      this.panel = [...Array(this.selectedSplitType.workouts.length).keys()]
      this.addWorkoutToSplit = false
      this.step = 4
    },
    selectSplitType() {
      const splitType = this.splitItems[this.selectedSplitIndex]
      this.selectedSplitType = JSON.parse(JSON.stringify(splitType))
      this.selectedSplitType.workouts.forEach((w) => this.$set(w, "exercises", []))
      this.panel = [...Array(this.selectedSplitType.workouts.length).keys()]
      this.step = 4
    },
    openAssignDialog(exercise, index) {
      this.exerciseToAssign = exercise
      this.exerciseToAssignIndex = index
      this.assignedSplits = this.selectedSplitType.workouts
        .map((split, splitIndex) => {
          return split.exercises.some((ex) => ex.instanceId === exercise.instanceId) ? splitIndex : null
        })
        .filter((i) => i !== null)
      this.assignDialog = true
    },
    // ERSETZEN SIE DIE KOMPLETTE METHODE
    // ERSETZEN SIE DIE KOMPLETTE assignExercise-METHODE MIT DIESEM CODE

    assignExercise() {
      // 1. Alle alten Zuweisungen für diese Übung entfernen
      this.selectedSplitType.workouts.forEach((split) => {
        const indexInSplit = split.exercises.findIndex((ex) => ex.instanceId === this.exerciseToAssign.instanceId)
        if (indexInSplit > -1) {
          split.exercises.splice(indexInSplit, 1)
        }
      })

      // 2. Übung den neu ausgewählten Splits zuweisen
      this.assignedSplits.forEach((splitIndex) => {
        this.selectedSplitType.workouts[splitIndex].exercises.push(this.exerciseToAssign)
      })

      // 3. 'Verfügbare' Liste aktualisieren
      if (this.assignedSplits.length > 0) {
        // **DIE WICHTIGSTE ÄNDERUNG:**
        // Wir ersetzen die Liste mit einer neuen, gefilterten Version.
        // Das ist für Vue zuverlässiger als das direkte Entfernen eines Elements.
        this.finalExercises = this.finalExercises.filter((ex) => ex.instanceId !== this.exerciseToAssign.instanceId)
      } else {
        // Stellt sicher, dass die Übung nicht bereits in der Liste ist, bevor sie hinzugefügt wird.
        const isAlreadyAvailable = this.finalExercises.some((ex) => ex.instanceId === this.exerciseToAssign.instanceId)
        if (!isAlreadyAvailable) {
          this.finalExercises.push(this.exerciseToAssign)
        }
      }

      this.assignDialog = false
    },
    // ERSETZEN SIE DIE KOMPLETTE METHODE
    unassignExercise(exercise, splitIndex, exerciseIndex) {
      // 1. Übung aus dem aktuellen Split entfernen
      this.selectedSplitType.workouts[splitIndex].exercises.splice(exerciseIndex, 1)

      // 2. Prüfen, ob die Übung noch in einem anderen Split vorhanden ist
      const isAssignedElsewhere = this.selectedSplitType.workouts.some((split) => split.exercises.some((ex) => ex.instanceId === exercise.instanceId))

      // 3. Wenn nicht, füge sie wieder zur Liste der verfügbaren Übungen hinzu
      if (!isAssignedElsewhere) {
        this.finalExercises.push(exercise)
      }
    },
    prepareAndRedirect() {
      if (this.finalExercises.length > 0) {
        this.showConfirmDialog = true
        return
      }
      this.proceedWithCreation()
    },
    confirmProceed() {
      this.showConfirmDialog = false
      this.proceedWithCreation()
    },
    proceedWithCreation() {
      this.selectedSplitType.workouts.forEach((split) => {
        split.exercises.forEach((exercise) => {
          if (exercise.sets && exercise.sets.length > 0 && exercise.sets[0].settings) {
            exercise.sets = exercise.sets.map((set) => set.settings)
          }
          if (exercise.sets && exercise.sets.length > 0) {
            const weights = exercise.sets.map((set) => parseFloat(set.find((item) => item.text === "Gewicht")?.value || 0)).filter((v) => !isNaN(v))
            const minReps = exercise.sets
              .map((set) => parseInt(set.find((item) => item.text === "Min Wiederholungen")?.value || 0))
              .filter((v) => !isNaN(v))
            const maxReps = exercise.sets
              .map((set) => parseInt(set.find((item) => item.text === "Max Wiederholungen")?.value || 0))
              .filter((v) => !isNaN(v))

            if (weights.length > 0) {
              exercise.weightSum = { min: Math.min(...weights), max: Math.max(...weights) }
            }
            if (minReps.length > 0 || maxReps.length > 0) {
              const allReps = [...minReps, ...maxReps]
              exercise.repSum = { min: Math.min(...allReps), max: Math.max(...allReps) }
            }
          }
        })
      })

      const newPlanForm = {
        title: `Konvertierter Plan - ${moment().format("DD.MM.YY")}`,
        ts: moment().unix().toString(),
        duration: 60,
        splits: this.selectedSplitType,
      }

      this.setPlanForConversion(newPlanForm)
      if (this.isStandalone) {
        this.$router.push({ name: "WorkoutCreateNew", params: { conversion: "true" } })
      } else {
        this.$emit("conversion-complete")
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.custom-split-dialog-card {
  // Hintergrund mit subtilem Noise-Effekt
  background-color: #1e1e1e !important;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='4' height='4' viewBox='0 0 4 4'%3E%3Cpath fill='%23343434' fill-opacity='0.4' d='M1 3h1v1H1V3zm2-2h1v1H3V1z'%3E%3C/path%3E%3C/svg%3E");
  border: 1px solid #444;
  border-radius: 16px !important;
  box-shadow: 0px 15px 30px rgba(0, 0, 0, 0.5);
}
.custom-chip {
  background: linear-gradient(135deg, var(--v-primary-base), var(--v-primary-darken2)) !important;
  font-weight: bold;
  color: white !important;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);

  .v-icon {
    color: white !important;
    opacity: 0.8;
    &:hover {
      opacity: 1;
    }
  }
}
.converter-background {
  background: linear-gradient(145deg, #2a2a2a, #1a1a1a);
  height: 100vh;
  width: 100vw;
  display: flex;
  flex-direction: column;
}
.step-wrapper {
  padding: 0 16px 100px 16px;
  display: flex;
  flex-direction: column;
  height: 100%;
}
.step-title {
  color: var(--v-primary);
  font-size: 2.5rem;
  font-weight: 800;
  line-height: 1.2;
  margin-bottom: 8px;
}
.step-subtitle {
  font-size: 1.1rem;
  color: #e8e7e7;
  margin-bottom: 24px;
}
.content-scroll {
  flex-grow: 1;
  overflow-y: auto;
  padding-right: 4px;
}
.step-actions {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 16px 24px;
  background: linear-gradient(to top, rgba(26, 26, 26, 1) 50%, rgba(26, 26, 26, 0));
}

.list-anim-enter-active,
.list-anim-leave-active {
  transition: all 0.5s cubic-bezier(0.55, 0, 0.1, 1);
}
.list-anim-enter,
.list-anim-leave-to {
  opacity: 0;
  transform: scale(0.95) translateY(20px);
}

.plan-item,
.split-item,
.glass-card {
  border-radius: 16px !important;
  background-color: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.2s ease-in-out;
  cursor: pointer;
}
.plan-item.v-list-item--active,
.split-item.split-item--active {
  background-color: rgba(0, 150, 63, 0.2);
  border-color: var(--v-primary-base);
  box-shadow: 0 0 20px rgba(0, 150, 63, 0.5);
}
.plan-item-light {
  border-radius: 16px !important;
  background-color: #f0f0f0;
  border: 1px solid #e0e0e0;
}
.plan-item--active {
  background-color: rgba(0, 150, 63, 0.2) !important;
  border-color: rgba(0, 150, 63, 0.2) !important;
}

.split-panel {
  background-color: rgba(255, 255, 255, 0.05) !important;
  border-radius: 16px !important;
}
.split-panel-header {
  font-size: 1.1rem;
}
.chip-container {
  flex-grow: 0;
  flex-shrink: 0;
}
.assigned-exercise-item {
  background-color: rgba(0, 0, 0, 0.2);
  padding: 8px 12px;
  border-radius: 12px;
  margin-bottom: 8px;
}
.exercise-card-source {
  cursor: pointer;
  transition: transform 0.2s ease;
}
.exercise-card-source:active {
  transform: scale(0.95);
}
.exercise-content {
  flex-grow: 1;
  padding: 0 16px;
  min-width: 0;
}

.assign-sheet {
  border-top-left-radius: 20px !important;
  border-top-right-radius: 20px !important;
}
.gradient-bg {
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  background: radial-gradient(circle at 15% 25%, rgba(0, 150, 63, 0.1), transparent 45%),
    radial-gradient(circle at 85% 75%, rgba(108, 99, 255, 0.1), transparent 45%);
  pointer-events: none;
}
.assigned-exercise-item {
  background-color: rgba(0, 0, 0, 0.2);
  padding: 8px 12px;
  border-radius: 12px;
  margin-bottom: 8px;
}
</style>
